import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  Coins, 
  Music, 
  CheckCircle, 
  Loader2, 
  Sparkles,
  ArrowLeft,
  Crown
} from "lucide-react";
import { toast } from "sonner";

export default function BuyCreditsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [packagesRes, creditsRes] = await Promise.all([
        axios.get(`${API}/credits/packages`),
        user ? axios.get(`${API}/credits`) : Promise.resolve({ data: null })
      ]);
      setPackages(packagesRes.data.packages || []);
      setCredits(creditsRes.data);
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to load credit packages");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId) => {
    if (!user) {
      toast.info("Please sign in to purchase credits");
      navigate("/login", { state: { from: "/buy-credits" } });
      return;
    }

    setPurchaseLoading(packageId);
    try {
      const response = await axios.post(`${API}/credits/purchase`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate purchase");
    } finally {
      setPurchaseLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="buy-credits-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" data-testid="buy-credits-page">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/20 rounded-full mb-4">
            <Coins className="h-8 w-8 text-gold" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-3">Buy Song Credits</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Purchase credits to stream individual songs without a subscription. 
            Perfect for occasional listeners.
          </p>
        </div>

        {/* Current Balance */}
        {user && credits && (
          <Card className="mb-8 bg-gold/5 border-gold/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Music className="h-5 w-5 text-gold" />
                  <span className="text-muted-foreground">Your Current Balance:</span>
                </div>
                <span className="font-bold text-xl text-gold" data-testid="current-credits">
                  {credits.audio_credits} streams
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Credit Packages */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {packages.map((pkg) => (
            <Card 
              key={pkg.id}
              className={`relative overflow-hidden transition-all hover:shadow-lg ${
                pkg.id === 'plus' ? 'border-gold ring-1 ring-gold/30' : ''
              }`}
              data-testid={`package-${pkg.id}`}
            >
              {pkg.id === 'plus' && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-gold text-primary-foreground">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Best Value
                  </Badge>
                </div>
              )}
              
              <CardHeader className="pb-4">
                <CardTitle className="font-serif text-2xl flex items-center gap-2">
                  <Music className="h-6 w-6 text-gold" />
                  {pkg.name}
                </CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold">${pkg.price}</span>
                  <span className="text-muted-foreground">one-time</span>
                </div>

                {/* Value Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>{pkg.audio_credits} song streams</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>${(pkg.price / pkg.audio_credits).toFixed(2)} per stream</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Credits never expire</span>
                  </div>
                </div>

                {/* Purchase Button */}
                <Button
                  className={`w-full ${
                    pkg.id === 'plus' 
                      ? 'bg-gold text-primary-foreground hover:bg-gold/90' 
                      : ''
                  }`}
                  variant={pkg.id === 'plus' ? 'default' : 'outline'}
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchaseLoading !== null}
                  data-testid={`purchase-${pkg.id}-btn`}
                >
                  {purchaseLoading === pkg.id ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Buy {pkg.audio_credits} Credits
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Subscription CTA */}
        <Card className="bg-gradient-to-r from-gold/10 to-amber-500/10 border-gold/30">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gold/20 rounded-full">
                  <Crown className="h-6 w-6 text-gold" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Want Unlimited Access?</h3>
                  <p className="text-muted-foreground text-sm">
                    Subscribe for $39/month and get unlimited streaming, downloads, and more.
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate("/subscribe")}
                className="bg-gold text-primary-foreground hover:bg-gold/90 whitespace-nowrap"
                data-testid="subscribe-cta"
              >
                View Subscription Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
